import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Autocomplete } from '@/modules/common';
import { OptionType } from '@/modules/common/types';
import { EventPrinterStatus } from '@/modules/events/constants';
import { EventPrinter } from '@/modules/events/types';

import { eventPrinterSchema } from '../schema';

interface IProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  printerOptions: OptionType[];
  selectedPrinter: EventPrinter | null;
  onAdd: (model: EventPrinter) => void;
  onEdit: (model: EventPrinter) => void;
}

export type EventPrinterValues = z.infer<typeof eventPrinterSchema>;

export const defaultPrinter = {
  name: '',
  printerId: '',
  type: '',
  status: EventPrinterStatus.Enabled,
};

export function AddEditPrinterDialog({
  isOpen,
  onOpenChange,
  printerOptions,
  selectedPrinter,
  onEdit,
  onAdd,
}: IProps) {
  const form = useForm<EventPrinterValues>({
    resolver: zodResolver(eventPrinterSchema),
    defaultValues: defaultPrinter,
  });
  const isEdit = selectedPrinter !== null;

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = form;

  const handleClose = async (open: boolean) => {
    onOpenChange(open);
    reset(defaultPrinter);
  };

  const handleFormSubmit = async (values: EventPrinter) => {
    if (isEdit) {
      onEdit(values);
    } else {
      onAdd(values);
    }
    handleClose(false);
  };

  useEffect(() => {
    if (isEdit) {
      reset(selectedPrinter);
    }
  }, [isEdit]);

  return (
    <>
      <Dialog onOpenChange={handleClose} open={isOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {isEdit ? 'Printer Details' : 'Add Printer'}
            </DialogTitle>
            <DialogDescription>Description</DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                event.stopPropagation();
                handleSubmit(handleFormSubmit)(event);
              }}
            >
              <div className="flex flex-col gap-4">
                <Autocomplete
                  label="Printer"
                  name={'printerId'}
                  control={control}
                  searchable
                  placeholder="Select printer"
                  options={printerOptions}
                />
                <FormField
                  control={control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Autocomplete
                  label="Status"
                  name={'status'}
                  control={control}
                  placeholder="Select status"
                  options={[
                    { id: EventPrinterStatus.Enabled, value: 'Enable' },
                    { id: EventPrinterStatus.Disabled, value: 'Disable' },
                  ]}
                />
              </div>
              <DialogFooter className="mt-6 gap-4">
                <DialogClose asChild>
                  <Button variant="outline" data-testid="cancel-button">
                    Cancel
                  </Button>
                </DialogClose>
                <Button
                  type="submit"
                  data-testid="save-button"
                  disabled={!isDirty}
                >
                  {isEdit ? 'Save' : 'Add Printer'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
}
